<template lang="pug">
v-expansion-panels#text-layer-editor(popout)
  name-editor(:layer="layer")

  dimension-editor(:dimensions="dimensions" :size="templateSize")

  v-expansion-panel.font-settings
    v-expansion-panel-header Font
    v-expansion-panel-content
      v-card
        v-card-text
          p Need inspiration? <a href="https://fonts.google.com" target="_blank">Browse Google Fonts</a>
          v-autocomplete(label="Font Family" v-model="textFontName" :items="allFonts" item-value="family" item-text="family" class="font-family-setting" dense outlined)
          v-autocomplete(label="Font Style" v-model="textFontStyle" :items="availableFontStyles" class="font-style-setting" dense outlined)
          v-text-field.text-size(label="Text Size" v-model="textSize" type="number" min="1" max="128" dense outlined)
            magic-property-input-talker(slot="prepend-inner" :layer="layer" attributeName="textSize")
          v-layout
            v-flex(shrink)
              magic-property-input-talker(slot="prepend-inner" :layer="layer" attributeName="textColor")
            v-flex
              color-picker(label="Text Color" v-model="textColor")
          v-divider.my-3
          p.subtitle-2.mb-1 Highlight Style
          p.caption.mb-2.grey--text These options apply to <span class="token-code">[h:colour:text]</span> highlighted text only. See the #[router-link(to="/help") documentation] for details.
          v-checkbox(v-model="highlightRounded" label="Rounded corners" hide-details dense)
          v-checkbox.mt-1(v-model="highlightPadding" label="Side padding" hide-details dense)

  v-expansion-panel
    v-expansion-panel-header Text Alignment
    v-expansion-panel-content
      v-card
        v-card-text
          p Horizontal Alignment
          magic-property-input-talker(:layer="layer" attributeName="horizontalAlignment")
          v-btn-toggle(v-model="horizontalAlignment")
            v-btn(small text value="left") Left
            v-btn(small text value="center") Center
            v-btn(small text value="right") Right
          p Vertical Alignment
          magic-property-input-talker(:layer="layer" attributeName="verticalAlignment")
          v-btn-toggle(v-model="verticalAlignment")
            v-btn(small text value="top") Top
            v-btn(small text value="middle") Middle
            v-btn(small text value="bottom") Bottom

  v-expansion-panel
    v-expansion-panel-header Inline Icons
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout(justify-space-between align-center)
            p.mb-0 Control the size and position of inline icons.
            v-btn(small text color="primary" @click="resetIconDefaults") Reset to defaults
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="iconScale"
              label="Icon Size"
              min="0.25"
              max="2.0"
              step="0.05"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.iconScale"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('iconScale', 0.25, 2.0, 2)"
              @blur="commitSliderInput('iconScale', 0.25, 2.0, 2)"
            )
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="iconVerticalOffset"
              label="Vertical Offset"
              min="-0.2"
              max="0.2"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.iconVerticalOffset"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('iconVerticalOffset', -0.2, 0.2, 2)"
              @blur="commitSliderInput('iconVerticalOffset', -0.2, 0.2, 2)"
            )
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="iconHorizontalGap"
              label="Horizontal Gap"
              min="0"
              max="1.0"
              step="0.05"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.iconHorizontalGap"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('iconHorizontalGap', 0, 1.0, 2)"
              @blur="commitSliderInput('iconHorizontalGap', 0, 1.0, 2)"
            )

  v-expansion-panel
    v-expansion-panel-header Corner Insets
    v-expansion-panel-content
      v-card
        v-card-text
          p.mb-0 Adjust corners to create non-rectangular text areas.
          v-btn(small text color="primary" @click="resetCornerDefaults") Reset to defaults
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="cornerTopLeft"
              label="Top Left"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.cornerTopLeft"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('cornerTopLeft', 0, 1, 2)"
              @blur="commitSliderInput('cornerTopLeft', 0, 1, 2)"
            )
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="cornerTopRight"
              label="Top Right"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.cornerTopRight"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('cornerTopRight', 0, 1, 2)"
              @blur="commitSliderInput('cornerTopRight', 0, 1, 2)"
            )
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="cornerBottomLeft"
              label="Bottom Left"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.cornerBottomLeft"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('cornerBottomLeft', 0, 1, 2)"
              @blur="commitSliderInput('cornerBottomLeft', 0, 1, 2)"
            )
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="cornerBottomRight"
              label="Bottom Right"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.cornerBottomRight"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('cornerBottomRight', 0, 1, 2)"
              @blur="commitSliderInput('cornerBottomRight', 0, 1, 2)"
            )

  v-expansion-panel
    v-expansion-panel-header Rotation
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="textRotation"
              label="Rotation"
              min="0"
              max="360"
              step="1"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="rotationInput"
              dense
              outlined
              hide-details
              suffix="°"
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitRotation"
              @blur="commitRotation"
            )
          v-layout(wrap)
            v-btn(small text @click="textRotation = 0") 0°
            v-btn(small text @click="textRotation = 45") 45°
            v-btn(small text @click="textRotation = 315") -45°
            v-btn(small text @click="textRotation = 90") 90°
            v-btn(small text @click="textRotation = 270") -90°
            v-btn(small text @click="textRotation = 180") 180°

  v-expansion-panel
    v-expansion-panel-header Text Content
    v-expansion-panel-content
      v-card
        v-card-text
          p(v-pre) Use triple curly-braces to reference columns, like: {{{Name}}}
          v-tooltip(top)
            | Name a column "{{ layer.name }}" and leave this blank to pull from your spreadsheet.
            template(v-slot:activator="{ on }")
              v-icon(v-on="on") mdi-table-search
          v-textarea.text-content(v-model="textContentTemplate" label="Text Template" filled :placeholder="`{{{${layer.name}}}}`")

  v-expansion-panel
    v-expansion-panel-header Opacity
    v-expansion-panel-content
      v-card
        v-card-text
          v-layout(align-center)
            v-slider.flex-grow-1(
              v-model="opacity"
              min="0"
              max="1"
              step="0.01"
              thumb-label
              filled
              hide-details
            )
            v-text-field(
              v-model="sliderInputs.opacity"
              dense
              outlined
              hide-details
              style="max-width: 76px; margin-left: 12px; flex-shrink: 0"
              @keydown.enter.prevent="commitSliderInput('opacity', 0, 1, 2)"
              @blur="commitSliderInput('opacity', 0, 1, 2)"
            )
</template>

<script>
  import { find } from 'lodash'
  import { mapActions, mapGetters } from 'vuex'
  import { computedVModelUpdateAll } from '../../util/component_helper'
  import NameEditor from './NameEditor.vue'
  import DimensionEditor from './DimensionEditor.vue'
  import ColorPicker from '../../shared/ColorPicker.vue'
  import MagicPropertyInputTalker from '../../source/MagicPropertyInputTalker.vue'

  export default {
    props: ["layer", "source"],

    data() {
      const l = this.layer
      return {
        rotationInput: String(l.textRotation ?? 0),
        sliderInputs: {
          iconScale:           String(l.iconScale           ?? 1),
          iconVerticalOffset:  String(l.iconVerticalOffset  ?? 0),
          iconHorizontalGap:   String(l.iconHorizontalGap   ?? 1),
          cornerTopLeft:       String(l.cornerTopLeft       ?? 0),
          cornerTopRight:      String(l.cornerTopRight      ?? 0),
          cornerBottomLeft:    String(l.cornerBottomLeft    ?? 0),
          cornerBottomRight:   String(l.cornerBottomRight   ?? 0),
          opacity:             String(l.opacity             ?? 1),
        }
      }
    },

    mounted() { this.fetchGoogleFonts() },

    components: {
      NameEditor,
      DimensionEditor,
      ColorPicker,
      MagicPropertyInputTalker,
    },

    computed: {
      ...mapGetters([
        "getLayerDimensions",
        "findTemplateByLayerId",
        "allFonts"
      ]),

      dimensions() { return this.getLayerDimensions(this.layer) },

      templateSize() {
        const template = this.findTemplateByLayerId(this.layer.id)
        return template && template.size
      },

      availableFontStyles() {
        const font = find(this.allFonts, { family: this.textFontName })
        return font && font.variants || []
      },

      propertyNames() {
        return this.$store.getters.sourceProperties(this.source)
      },

      highlightRounded: {
        get() { return this.layer.highlightRounded || false },
        set(v) { this.$store.dispatch('patchLayer', { id: this.layer.id, highlightRounded: v }) }
      },
      highlightPadding: {
        get() { return this.layer.highlightPadding || false },
        set(v) { this.$store.dispatch('patchLayer', { id: this.layer.id, highlightPadding: v }) }
      },

      ...computedVModelUpdateAll("layer", "patchLayer", [
        "textFontName",
        "textFontStyle",
        "textContentTemplate",
        "textColor",
        "textSize",
        "horizontalAlignment",
        "verticalAlignment",
        "iconScale",
        "iconVerticalOffset",
        "iconHorizontalGap",
        "cornerTopLeft",
        "cornerTopRight",
        "cornerBottomLeft",
        "cornerBottomRight",
        "textRotation",
        "opacity",
      ])
    },

    methods: {
      ...mapActions(["patchLayer", "fetchGoogleFonts"]),

      commitSliderInput(prop, min, max, decimals) {
        const parsed = parseFloat(this.sliderInputs[prop])
        if (!isNaN(parsed)) {
          const clamped = Math.min(max, Math.max(min, parsed))
          const rounded = parseFloat(clamped.toFixed(decimals))
          this[prop] = rounded
          this.sliderInputs[prop] = String(rounded)
        } else {
          this.sliderInputs[prop] = String(this[prop])
        }
      },

      commitRotation() {
        const parsed = parseFloat(this.rotationInput)
        if (!isNaN(parsed)) {
          const normalized = ((Math.round(parsed) % 360) + 360) % 360
          this.textRotation = normalized
          this.rotationInput = String(normalized)
        } else {
          this.rotationInput = String(this.textRotation)
        }
      },

      resetCornerDefaults() {
        this.$store.dispatch('patchLayer', {
          id: this.layer.id,
          cornerTopLeft: 0,
          cornerTopRight: 0,
          cornerBottomLeft: 0,
          cornerBottomRight: 0
        })
      },

      resetIconDefaults() {
        this.$store.dispatch('patchLayer', {
          id: this.layer.id,
          iconScale: 1,
          iconVerticalOffset: 0,
          iconHorizontalGap: 1
        })
      },

      ensureValidStyle() {
        this.$nextTick(() => {
          if(this.availableFontStyles.indexOf(this.textFontStyle) == -1) {
            this.textFontStyle = this.availableFontStyles[0]
          }
        })
      },
    },

    watch: {
      availableFontStyles: "ensureValidStyle",
      textRotation(val)         { this.rotationInput = String(val) },
      iconScale(val)            { this.sliderInputs.iconScale = String(val) },
      iconVerticalOffset(val)   { this.sliderInputs.iconVerticalOffset = String(val) },
      iconHorizontalGap(val)    { this.sliderInputs.iconHorizontalGap = String(val) },
      cornerTopLeft(val)        { this.sliderInputs.cornerTopLeft = String(val) },
      cornerTopRight(val)       { this.sliderInputs.cornerTopRight = String(val) },
      cornerBottomLeft(val)     { this.sliderInputs.cornerBottomLeft = String(val) },
      cornerBottomRight(val)    { this.sliderInputs.cornerBottomRight = String(val) },
      opacity(val)              { this.sliderInputs.opacity = String(val) },
    }
  }
</script>

<style scoped>
  textarea {
    height: 200px;
  }
  .token-code {
    font-family: 'Courier New', monospace;
    font-size: 11px;
    background: rgba(128,128,128,0.15);
    padding: 1px 4px;
    border-radius: 3px;
  }
</style>